import React, { useState } from 'react';
import { Text, Icon, Button } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { DevProject } from '../../types/chat';
import { IfHasPermission } from '../IfHasPermission';
import { Permissions } from '../../constants';
import { AppPosition, EnvironmentRuntime, ProjectEnvironment } from '../../services/projectEnvironment';
import { formatDate } from '../../utils/dateUtils';
import {
  deployActionLabel,
  displayEnvironmentName,
  isBusyStatus,
  isDeployed,
  isFailedStatus,
  isSub,
  parentOf,
  checkpointTitle,
  pipelineNarrative,
  runtimeAction,
  urlLabel,
} from './types';
import { EnvironmentBadge } from './StatusBadge';

/** A commit, as the short hash with its message a hover away.
 *
 * The two version rows used to be a boxed block that spelled each side out
 * over two lines — a label, a hash, and the checkpoint title underneath. That
 * block is gone: a checkpoint title is a whole sentence the agent wrote, so
 * five of them stacked read as prose, and the block changed shape depending on
 * what someone happened to type. As a plain row the panel keeps one rhythm and
 * the sentence is still one hover away.
 *
 * The tooltip falls back to the full 40 characters when a commit has no
 * checkpoint title — a `/git/push`, the initial commit and an external pull
 * all have none — so the hover is never empty, and what it gives you pastes
 * straight into `git show`. */
const CommitCell: React.FC<{
  hash?: string | null;
  title?: string | null;
  /** Stands in for the hash when there is none — "Not deployed yet". */
  fallback: string;
}> = ({ hash, title, fallback }) =>
  hash ? (
    // `as="span"`: the row's cell is a block, and a block Text would make the
    // whole width of it hoverable — the tooltip would appear over empty space
    // to the right of a seven-character hash.
    <Text
      as="span"
      variant="text-sm"
      color="primary"
      className="font-mono"
      title={title || hash}
    >
      {hash.slice(0, 7)}
    </Text>
  ) : (
    <Text variant="text-sm" color="tertiary">
      {fallback}
    </Text>
  );

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-start gap-bg-v2-md py-bg-v2-sm w-full">
    <Text variant="text-sm" color="secondary" className="w-[130px] shrink-0">
      {label}
    </Text>
    <div className="flex-1 min-w-0">{children}</div>
  </div>
);

interface EnvironmentDetailsViewProps {
  environment: ProjectEnvironment;
  /** Live container state for this environment, when known. */
  runtime?: EnvironmentRuntime;
  /** True while this environment's start/stop request is in flight. */
  runtimeActionPending: boolean;
  checkingDeploy: boolean;
  deleting: boolean;
  isBuilding: boolean;
  /** True while any environment of this project is building — only one deploy runs at a time. */
  /** Another environment of this project is mid-deploy. Only a deploy blocks a
   *  deploy — a `creating` sibling does no build and holds nothing up. */
  deployLocked: boolean;
  /** Every environment of this project — used to name the main a sub promotes from. */
  environments: ProjectEnvironment[];
  /** Where the project's own code stands. The other side of a main's comparison. */
  currentApp: AppPosition | null;
  onStartStop: (action: 'start' | 'stop') => void;
  onDeploy: () => void;
  onEdit: () => void;
  onDelete: () => void;
  project: DevProject | null;
}

/** Read-only detail screen for one environment + its Deploy/Edit/Delete actions. */
export const EnvironmentDetailsView: React.FC<EnvironmentDetailsViewProps> = ({
  environment,
  runtime,
  runtimeActionPending,
  checkingDeploy,
  deleting,
  isBuilding,
  deployLocked,
  environments,
  currentApp,
  onStartStop,
  onDeploy,
  onEdit,
  onDelete,
  project,
}) => {
  const [copiedError, setCopiedError] = useState(false);
  const handleCopyErrorDetail = async () => {
    if (!environment.error_detail) return;
    try {
      await navigator.clipboard.writeText(environment.error_detail);
      setCopiedError(true);
      setTimeout(() => setCopiedError(false), 2000);
    } catch (err) {
      console.error('Failed to copy error details:', err);
    }
  };

  const busy = isBusyStatus(environment.status);
  const deployed = isDeployed(environment.status);
  const mutationsLocked = busy || deleting;
  const action = deployed && !busy ? runtimeAction(runtime, environment.status) : null;
  const actionPending = runtimeActionPending || runtime?.in_progress === true;
  const deployDisabled =
    mutationsLocked || checkingDeploy || isBuilding || deployLocked || actionPending;
  const stopped = deployed && runtime?.status === 'stopped';

  // A sub runs its main's code, never the project's, so the one deploy action
  // is named after where the code comes from.
  const sub = isSub(environment);
  const main = parentOf(environment, environments);
  const deployLabel = deployActionLabel(environment, environments);
  // A rollback moved the source back past this environment: it is running work
  // the source has abandoned. Deploying replaces it — the same warning as
  // diverged, for the same reason. Both states turn the position callout red.
  const losesWork =
    environment.pipeline_state === 'diverged' || environment.pipeline_state === 'ahead';
  // Never `deployed_commit_hash` on its own: when an environment was deployed
  // from a rollback the stored hash is the rollback commit, and `commit_hash`
  // is the tree it actually restored.
  const ownHash = environment.commit_hash || environment.deployed_commit_hash;
  const title = checkpointTitle(environment);
  // One at most — the unique index on parent_environment_id is what makes a
  // pipeline a chain rather than a tree.
  const successor = environments.find((e) => e.parent_environment_id === environment.id);

  // The position, as a sentence. It used to be a `relation` fragment inside a
  // block that named both sides ("1 change behind"); with the block gone the
  // line has to name them itself, and there is room to end on what the button
  // would do about it.
  const narrative = pipelineNarrative(environment, environments, currentApp);
  // The other side of the comparison: a sub is measured against its main, a
  // main against the project itself.
  const sourceHash = sub ? main?.commit_hash || main?.deployed_commit_hash : currentApp?.commit_hash;
  const sourceTitle = sub ? main?.checkpoint_title : currentApp?.checkpoint_title;
  const sourceFallback = sub
    ? main
      ? 'Not deployed yet'
      : 'No longer exists'
    : 'Unknown';
  // The backend refuses this with 400; saying so up front beats a failed click.
  const mainNotDeployed = sub && !main?.deployed_commit_hash;

  const deployTitle = isBuilding
    ? 'Unavailable while a build is running'
    : deployLocked
    ? 'Another deploy is already running for this project'
    : actionPending
    ? 'Unavailable while the environment is starting or stopping'
    : mainNotDeployed
    ? main
      ? `${displayEnvironmentName(main.environment_name)} has not been deployed yet, so there is nothing to promote`
      : 'The environment this promotes from no longer exists'
    : sub && main
    ? `Copy the code ${displayEnvironmentName(main.environment_name)} is running into this environment`
    : 'Deploy current changes';

  return (
    <div className="flex flex-col gap-bg-v2-lg w-full">
      {/* Status + tiny actions */}
      <div className="flex items-center gap-bg-v2-md">
        <div className="flex items-center gap-bg-v2-md flex-wrap flex-1 min-w-0">
          <EnvironmentBadge status={environment.status} runtime={runtime} />
        </div>

        <IfHasPermission permissions={project?.permissions} requires={Permissions.UpdateProjectPermission}>
          <div className="flex items-center gap-bg-v2-xs shrink-0">
            <button
              onClick={onEdit}
              disabled={mutationsLocked}
              title={busy ? 'Unavailable while a build is running' : 'Edit environment'}
              className="flex items-center justify-center w-[28px] h-[28px] rounded-full hover:bg-bg-secondary-hover transition-colors disabled:opacity-50"
            >
              <Icon name={IconName.Edit} size="xs" className="text-text-tertiary" inheritColor />
            </button>
            <button
              onClick={onDelete}
              disabled={mutationsLocked}
              title={busy ? 'Unavailable while a build is running' : 'Delete environment'}
              className="flex items-center justify-center w-[28px] h-[28px] rounded-full hover:bg-bg-error-primary transition-colors disabled:opacity-50"
            >
              <Icon name={deleting ? IconName.Loading : IconName.Delete} size="xs" spin={deleting} className="text-text-tertiary hover:text-text-error-primary transition-colors" inheritColor />
            </button>
          </div>
        </IfHasPermission>
      </div>

      {/* URL — hidden entirely when the environment failed */}
      {deployed && environment.url && (
        <a
          href={environment.url}
          target="_blank"
          rel="noopener noreferrer"
          // `self-start` is load-bearing, not decoration. The row is a column
          // flex container, so `align-items: stretch` blockifies this anchor
          // and stretches it across the full row width regardless of
          // `inline-flex` — which made the whole line a link, and clicking to
          // the left or right of the address opened the app instead of the
          // environment. Shrunk to its content, the link is only where the
          // text is.
          className="inline-flex self-start items-center gap-bg-v2-xs text-border-brand hover:underline min-w-0 max-w-full"
        >
          {/* min-w-0 so it can still shrink now that the anchor is
              fit-content: a flex child defaults to min-width:auto, which
              refuses to go below its text and would defeat the truncation. */}
          <Text variant="text-sm" className="!text-border-brand truncate min-w-0">
            {urlLabel(environment.url)}
          </Text>
          <Icon name={IconName.ExternalLink} size="xs" inheritColor className="shrink-0" />
        </a>
      )}
      {busy && (
        <Text variant="text-sm" color="tertiary" className="truncate max-w-full">
          {environment.url ? `${urlLabel(environment.url)} · available once deployed` : 'URL available once deployed'}
        </Text>
      )}

      {busy && (
        <div className="flex items-start gap-bg-v2-xs p-bg-v2-md bg-bg-secondary border border-border-secondary rounded-bg-v2-md">
          <Icon name={IconName.Loading} size="sm" spin className="text-text-brand-primary shrink-0 mt-bg-v2-xxs" inheritColor />
          <Text variant="text-xs" color="secondary" className="break-words">
            A deploy is running. This involves a Docker build and can take several minutes.
          </Text>
        </div>
      )}

      {isFailedStatus(environment.status) && (
        <div className="flex flex-col gap-bg-v2-sm p-bg-v2-md bg-bg-error-primary border border-border-error-subtle rounded-bg-v2-sm">
          <div className="flex items-start gap-bg-v2-xs">
            <Icon name={IconName.Alert} size="sm" className="text-text-error-primary shrink-0 mt-bg-v2-xxs" inheritColor />
            <Text variant="text-xs" color="error" className="break-words">
              {environment.error ||
                'The last deploy failed. Fix the project code and deploy again.'}
            </Text>
          </div>

          {/* The raw build output is a wall of compiler noise — too wide for a
              480px panel to render usefully. Copying it out lets the user paste
              it somewhere that can actually display it, or hand it to support. */}
          {environment.error_detail && (
            <div className="flex items-start">
              <Button
                variant="link"
                size="small"
                onClick={handleCopyErrorDetail}
                title="Copy the full build output to the clipboard"
                label={
                  <div className="flex items-center gap-bg-v2-xs">
                    <Icon
                      name={copiedError ? IconName.Check : IconName.Copy}
                      size="sm"
                      inheritColor
                    />
                    <span>{copiedError ? 'Copied' : 'Copy error details'}</span>
                  </div>
                }
              />
            </div>
          )}
        </div>
      )}

      {/* Where this environment stands against its source, and what the
          button below would do about it.
        *
        * This replaces the boxed Version block that used to sit in the middle
        * of the panel with its conclusion on the last line. The conclusion is
        * the part anyone reads, so it comes first and in a sentence; the two
        * hashes it talks about are rows in Details like everything else.
        *
        * Suppressed mid-deploy — the position is being changed as you look at
        * it, and the deploy callout above already says so.
        *
        * Two treatments, not three: grey when a deploy only moves this
        * environment forward, red when it would throw work away. That is the
        * one distinction the reader has to act on, and `pipelineTone`'s third
        * step exists for the list row, where a line has to stand out among
        * others without a box around it. */}
      {!busy && narrative && (
        <div
          className={`flex items-start gap-bg-v2-xs p-bg-v2-md border rounded-bg-v2-md ${
            losesWork
              ? 'bg-bg-error-primary border-border-error-subtle'
              : 'bg-bg-secondary border-border-secondary'
          }`}
        >
          <Icon
            name={losesWork ? IconName.Alert : IconName.InfoCircle}
            size="sm"
            className={`shrink-0 mt-bg-v2-xxs ${
              losesWork ? 'text-text-error-primary' : 'text-text-tertiary'
            }`}
            inheritColor
          />
          <Text
            variant="text-xs"
            color={losesWork ? 'error' : 'secondary'}
            className="break-words"
          >
            {narrative}
          </Text>
        </div>
      )}

      {/* Details */}
      <div className="flex flex-col w-full">
        <Text variant="text-sm" weight="medium" color="primary" className="mb-bg-v2-xs">
          Details
        </Text>
        {sub && (
          <DetailRow label="Promotes from">
            <Text variant="text-sm" color="primary">
              {main ? displayEnvironmentName(main.environment_name) : 'No longer exists'}
            </Text>
          </DetailRow>
        )}
        {successor && (
          <DetailRow label="Promotes to">
            <Text variant="text-sm" color="primary" className="break-words">
              {displayEnvironmentName(successor.environment_name)}
            </Text>
          </DetailRow>
        )}
        {/* Two flat rows where a boxed block used to be, and no "Version" /
            "History" headings around them. Three headed sections in a 480px
            panel meant every fact arrived with a category attached to it; the
            categories were doing no work that the row labels were not already
            doing, and they made the panel read as a form rather than a list of
            facts. */}
        <DetailRow label="Version">
          <CommitCell hash={ownHash} title={title} fallback="Not deployed yet" />
        </DetailRow>
        <DetailRow label="Source version">
          <CommitCell hash={sourceHash} title={sourceTitle} fallback={sourceFallback} />
        </DetailRow>
        <DetailRow label="Deployed at">
          <Text variant="text-sm" color="primary">
            {environment.deployed_at ? formatDate(environment.deployed_at) : 'Not deployed yet'}
          </Text>
        </DetailRow>

        {environment.updated_by && (
          <DetailRow label="Last deployed by">
            <Text variant="text-sm" color="primary" className="break-words">
              {environment.updated_by}
            </Text>
          </DetailRow>
        )}
        {environment.created_by && (
          <DetailRow label="Created by">
            <Text variant="text-sm" color="primary" className="break-words">
              {environment.created_by}
            </Text>
          </DetailRow>
        )}
        <DetailRow label="Created">
          <Text variant="text-sm" color="primary">
            {environment.created_at ? formatDate(environment.created_at) : '—'}
          </Text>
        </DetailRow>
      </div>

      {/* Beside the button that fixes it, not at the top of the panel.
        *
        * This is the mildest thing the panel ever has to say — the app is fine,
        * it is just not running — and the remedy is one button below it. Given
        * a box at the top it outranked the position callout, which is the line
        * you are actually here to read. As a plain line it is a footnote to
        * Start, which is what it is.
        *
        * The red "running changes that were rolled back" paragraph that used to
        * sit here went the other way, up into the position callout: it is about
        * what the deploy would cost, so it belongs with the rest of that
        * sentence rather than repeated at both ends of the screen. */}
      {stopped && (
        <div className="flex items-start gap-bg-v2-xs">
          <Icon
            name={IconName.InfoCircle}
            size="sm"
            className="text-text-tertiary shrink-0 mt-bg-v2-xxs"
            inheritColor
          />
          <Text variant="text-xs" color="tertiary" className="break-words">
            Stopped, so the URL won't respond. Starting brings the app back up in seconds — its
            data is kept.
          </Text>
        </div>
      )}

      {/* Start/Stop and Deploy are gated separately, because they are not the
          same privilege. An admin is not a member of this project, so their
          permissions read as read-only, yet /start and /stop accept them —
          hence `allowAdmin` on the first and not the second. Running and
          stopping is operational; deploying changes what the environment is. */}
      <div className="flex justify-end gap-bg-v2-md pt-bg-v2-md border-t border-border-secondary">
        <IfHasPermission
          permissions={project?.permissions}
          requires={Permissions.UpdateProjectPermission}
          allowAdmin
        >
          {deployed && (action || actionPending) && (
            <Button
              variant={action === 'stop' || runtime?.status === 'stopping' ? 'error' : 'success'}
              size="small"
              onClick={() => action && onStartStop(action)}
              disabled={!action || actionPending}
              loading={actionPending}
              title={
                actionPending || !action
                  ? 'Start or stop in progress'
                  : action === 'start'
                  ? 'Start this environment'
                  : 'Stop this environment — its data is kept'
              }
              label={
                <div className="flex items-center gap-bg-v2-xs">
                  {!actionPending && (
                    <Icon
                      name={action === 'start' ? IconName.Run : IconName.Stop}
                      size="sm"
                      inheritColor
                    />
                  )}
                  <span>
                    {actionPending
                      ? runtime?.status === 'stopping'
                        ? 'Stopping…'
                        : 'Starting…'
                      : action === 'start'
                      ? 'Start'
                      : 'Stop'}
                  </span>
                </div>
              }
            />
          )}
        </IfHasPermission>

        {/* No `allowAdmin`: deploying rewrites the environment's code. */}
        <IfHasPermission
          permissions={project?.permissions}
          requires={Permissions.UpdateProjectPermission}
        >
          <Button
            variant="primary"
            size="small"
            onClick={onDeploy}
            disabled={deployDisabled || mainNotDeployed}
            loading={checkingDeploy}
            title={deployTitle}
            label={
              <div className="flex items-center gap-bg-v2-xs">
                <Icon name={IconName.Push} size="sm" inheritColor />
                <span>{checkingDeploy ? 'Checking…' : deployLabel}</span>
              </div>
            }
          />
        </IfHasPermission>
      </div>
    </div>
  );
};

export default EnvironmentDetailsView;
